import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, DownIcon, IconButtonWrapper, UpIcon } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, ETimePeriod, formatNumber, formatToDatePeriod, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { VideoModal } from '../../components/VideoModal';
import MTooltip from '@material-ui/core/Tooltip';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import { ChartInfo } from '../../components/ChartInfo';

import { getBudgetProgressColor, RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartNotes } from '../ChartNotes';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
            padding: '0 15px',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 15,
                flexFlow: 'row wrap',
            },
            '& .col': {
                fontSize: '16px',
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                padding: '15px',
                display: 'flex',
                margin: '0 10px 10px 10px',
                marginBottom: '10px',
                flexFlow: 'column',
                flex: 1,
                '& .value': {
                    fontSize: '36px',
                    padding: '20px 0',
                },
                '& .sub': {
                    display: 'flex',
                    flexFlow: 'row wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
                '& .status': {
                    marginLeft: '10px',
                    display: 'flex',
                    flexFlow: 'row wrap',
                    alignItems: 'center',
                },
            },
            '& .increase': {
                color: 'green',
            },
            '& .decrease': {
                color: 'red',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
        },
        span: {
            fontSize: '1rem',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            display: 'flex',
            alignItems: 'center',
            margin: '0 -2.5px',
            '& > *': {
                margin: '0 2.5px',
            },
            '@media print': {
                display: 'none !important',
            },
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)'
        },
    }),
);

export enum OptionsEnum {
    MONTH,
    PREVIOUS_MONTH,
    FINANCIAL_YEAR,
    PREV_FINANCIAL_YEAR,
}

interface FeesBilledVsBudgetPartChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

interface ChartState {
    options: OptionsEnum;
    months: string[];
    counts: number[];
    budgetCounts: number[];
    currentYearTotal: number;
    currentTotal: number;

    budgetTotal: number;
    varianceTotal: number;
    percentageTotal: number;

    budgetYearTotal: number;
    varianceYearTotal: number;
    percentageYearTotal: number;

    leftPeriodString: string;
    rightPeriodString: string;
    symbol: string;
}

export const FeesBilledVsBudgetPartChart: React.FC<FeesBilledVsBudgetPartChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const feesBilledVsBudget = useQuery<FeesBilledVsBudgetQueryData>(FeesBilledVsBudgetQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        options: OptionsEnum.MONTH,
        months: [],
        counts: [],
        budgetCounts: [],
        currentYearTotal: 0,
        currentTotal: 0,
        budgetTotal: 0,
        varianceTotal: 0,
        percentageTotal: 0,
        budgetYearTotal: 0,
        varianceYearTotal: 0,
        percentageYearTotal: 0,
        leftPeriodString: formatToDatePeriod('monthToDate'),
        rightPeriodString: formatToDatePeriod('yearToDateFees'),
        symbol: '$'
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        setState((prevState) => {
            let months: string[] = [];
            let counts: number[] = [];
            let budgetCounts: number[] = [];
            let currentTotal: number = 0;
            let currentYearTotal: number = 0;
            let leftPeriodString: string = '';
            let rightPeriodString: string = formatToDatePeriod('yearToDateFees');

            let budgetTotal = 0;
            let varianceTotal = 0;
            let percentageTotal = 0;
            let budgetYearTotal = 0;
            let varianceYearTotal = 0;
            let percentageYearTotal = 0;

            if (
                !feesBilledVsBudget.loading &&
                !feesBilledVsBudget.error &&
                feesBilledVsBudget.data &&
                feesBilledVsBudget.data.statistics &&
                feesBilledVsBudget.data.statistics.fees
            ) {
                var lastMonth = feesBilledVsBudget.data.statistics.fees.feesBilledVsBudget.lastMonth;
                var previousMonth = feesBilledVsBudget.data.statistics.fees.feesBilledVsBudget.previousMonth;
                var lastYear = feesBilledVsBudget.data.statistics.fees.feesBilledVsBudget.lastFinancialYear;
                var prevYear = feesBilledVsBudget.data.statistics.fees.feesBilledVsBudget.previousFinancialYear;

                currentYearTotal = lastYear.summary.lastPeriodTotal;
                budgetYearTotal = lastYear.summary.budget;
                varianceYearTotal = lastYear.summary.variance;
                percentageYearTotal = Math.round(lastYear.summary.percentage);

                if (state.options === OptionsEnum.MONTH) {
                    months = lastMonth.feesBilledGrouping.map((s) => s.group);
                    counts = lastMonth.feesBilledGrouping.map((s) => s.value);
                    budgetCounts = lastMonth.feesBudgetGrouping.map((s) => s.value);

                    currentTotal = lastMonth.summary.lastPeriodTotal;
                    budgetTotal = lastMonth.summary.budget;
                    varianceTotal = lastMonth.summary.variance;
                    percentageTotal = Math.round(lastMonth.summary.percentage);
                    leftPeriodString = formatToDatePeriod('monthToDate');
                } else if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                    months = previousMonth.feesBilledGrouping.map((s) => s.group);
                    counts = previousMonth.feesBilledGrouping.map((s) => s.value);
                    budgetCounts = previousMonth.feesBudgetGrouping.map((s) => s.value);

                    currentTotal = previousMonth.summary.lastPeriodTotal;
                    budgetTotal = previousMonth.summary.budget;
                    varianceTotal = previousMonth.summary.variance;
                    percentageTotal = Math.round(previousMonth.summary.percentage);

                    if (previousMonth.financialYearToPreviousMonthSummary) {
                        currentYearTotal = previousMonth.financialYearToPreviousMonthSummary.lastPeriodTotal;
                        budgetYearTotal = previousMonth.financialYearToPreviousMonthSummary.budget;
                        varianceYearTotal = previousMonth.financialYearToPreviousMonthSummary.variance;
                        percentageYearTotal = Math.round(previousMonth.financialYearToPreviousMonthSummary.percentage);
                    } else {
                        currentYearTotal = 0;
                        budgetYearTotal = 0;
                        varianceYearTotal = 0;
                        percentageYearTotal = 0;
                    }

                    leftPeriodString = formatToDatePeriod('previousMonth');
                    rightPeriodString = formatToDatePeriod('previousMonthYearToDateFees');
                } else if (state.options === OptionsEnum.FINANCIAL_YEAR) {
                    months = lastYear.feesBilledGrouping.map((s) => s.group);
                    counts = lastYear.feesBilledGrouping.map((s) => s.value);
                    budgetCounts = lastYear.feesBudgetGrouping.map((s) => s.value);

                    currentTotal = lastYear.summary.lastPeriodTotal;
                    budgetTotal = lastYear.summary.budget;
                    varianceTotal = lastYear.summary.variance;
                    percentageTotal = Math.round(lastYear.summary.percentage);
                    leftPeriodString = formatToDatePeriod('yearToDate');
                } else {
                    months = prevYear.feesBilledGrouping.map((s) => s.group);
                    counts = prevYear.feesBilledGrouping.map((s) => s.value);
                    budgetCounts = prevYear.feesBudgetGrouping.map((s) => s.value);

                    currentTotal = prevYear.summary.lastPeriodTotal;
                    budgetTotal = prevYear.summary.budget;
                    varianceTotal = prevYear.summary.variance;
                    percentageTotal = Math.round(prevYear.summary.percentage);
                    leftPeriodString = formatToDatePeriod('previousYearToDate');
                }
            }

            return {
                ...prevState,
                months,
                counts,
                budgetCounts,
                currentTotal,
                currentYearTotal,
                budgetTotal,
                varianceTotal,
                budgetYearTotal,
                varianceYearTotal,
                percentageTotal,
                percentageYearTotal,
                leftPeriodString,
                rightPeriodString,
            };
        });
    // tslint:disable-next-line: align
    }, [state.options, feesBilledVsBudget.loading, feesBilledVsBudget.error, feesBilledVsBudget.data]);

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isFeesBilledVsBudgetPartChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                options: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isMonth: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let defaultView = 'Fees Billed MTD/YTD';
            let timePeriod = ETimePeriod.THIS_MONTH;

            if (isMonth) {
                if (state.options === OptionsEnum.MONTH) {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.THIS_MONTH;
                } else if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'prev' });
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                } else if (state.options === OptionsEnum.FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.FINANCIAL_YEAR;
                } else if (state.options === OptionsEnum.PREV_FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'prev' });
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_FINANCIAL_YEAR;
                }
            } else {
                var period = 'curr';
                timePeriod = ETimePeriod.FINANCIAL_YEAR;
                if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                    period = 'prevMonth';
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                }

                searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: period });
            }

            const title = `Drilldown from Fees Billed vs Budget for ${timePeriod}`;

            props.getSelectedItem(searchCriterias, false, title, DrillDownType.staffBillingSummary, defaultView);
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };
    
    if (currencySymbolQuery.loading || feesBilledVsBudget.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.budgetCounts, state.counts) ? ' rv-chart--empty' : ''}`}>
            {isVideoModalOpen && props.infoUrl && (
                <VideoModal 
                    open={isVideoModalOpen}
                    onClose={onToggleVideo}
                    url={props.infoUrl}
                />
            )}
            <IconButtonWrapper aria-label="close">
                <div
                    className={`${classes.helpIcon} rv-chart-help`} 
                >
                    <ChartInfo 
                        description={props.description}
                        fontSize={props.showAllRecords ? 'large' : 'medium'}
                        // className={classes.helpIcon}
                    />
                    {props.infoUrl && (
                        <MTooltip title="Learn more">
                            <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                <VideoLibraryOutlinedIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                </div>
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<MonetizationOnOutlinedIcon />} title="Fees Billed vs Budget" />
                <div className="colWrapper">
                    <div className="col">
                        <div className="title">{state.leftPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(true)}>
                            <AnimatedNumber value={state.currentTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.percentageTotal))}%`, backgroundColor: getBudgetProgressColor(state.percentageTotal) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(true)}>
                            {`Budget ${state.symbol}${formatNumber(state.budgetTotal)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(true)}>
                                {`${formatNumber(state.percentageTotal)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budgetTotal < state.currentTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(true)} title="Variance">
                                {state.budgetTotal < state.currentTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${state.symbol}${formatNumber(state.varianceTotal)}`}
                            </div>
                        </div>
                    </div>
                    <div className="col">
                        <div className="title">{state.rightPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(false)}>
                            <AnimatedNumber value={state.currentYearTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.percentageYearTotal))}%`, backgroundColor: getBudgetProgressColor(state.percentageYearTotal) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(false)}>
                            {`Budget ${state.symbol}${formatNumber(state.budgetYearTotal)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(false)}>
                                {`${formatNumber(state.percentageYearTotal)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budgetYearTotal < state.currentYearTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(false)} title="Variance">
                                {state.budgetYearTotal < state.currentYearTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${state.symbol}${formatNumber(state.varianceYearTotal)}`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            // const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            // const item = state.months[_index];

                            // const searchCriterias = new Array<SearchCriteria>();

                            // if (state.options === OptionsEnum.MONTH) {
                            //     searchCriterias.push(
                            //         { property: 'DateShortString', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May 05
                            //     );
                            // } else if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                            //     searchCriterias.push(
                            //         { property: 'DateShortString', operator: 'eq', comparison: item, period: 'prev' }, // e.g. Apr 05
                            //     );
                            // } else if (state.options === OptionsEnum.FINANCIAL_YEAR) {
                            //     searchCriterias.push(
                            //         { property: 'Month', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May
                            //     );
                            // } else {
                            //     searchCriterias.push(
                            //         { property: 'Month', operator: 'eq', comparison: item, period: 'prev' }
                            //     );
                            // }

                            // props.getSelectedItem(
                            //     searchCriterias,
                            //     false,
                            //     'Fees Billed vs Budget - ' + item,
                            //     DrillDownType.staffBillingSummary,
                            //     state.options === OptionsEnum.MONTH
                            //         ? 'Fees Billed WTD'
                            //         : 'Fees Billed MTD/YTD'
                            // );
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                label: 'Budget',
                                fill: false,
                                backgroundColor: RV_CHART_COLORS.amber,
                                borderColor: RV_CHART_COLORS.amber,
                                borderWidth: 3,
                                hoverBackgroundColor: RV_CHART_COLORS.amber,
                                hoverBorderColor: RV_CHART_COLORS.amber,
                                tension: 0.35,
                                pointRadius: 0,
                                data: state.budgetCounts,
                                type: 'line',
                                pointStyle: 'line',
                            },
                            {
                                fill: false,
                                label: 'Fees Billed',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.counts,
                                type: 'bar',
                            },
                        // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.budgetCounts, ...state.counts]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.budgetCounts, ...state.counts]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.budgetCounts, state.counts) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={OptionsEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={OptionsEnum.PREVIOUS_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                    <FormControlLabel value={OptionsEnum.FINANCIAL_YEAR} control={<Radio color="primary" />} label="Financial Year" />
                    <FormControlLabel value={OptionsEnum.PREV_FINANCIAL_YEAR} control={<Radio color="primary" />} label="Previous Financial Year" />
                </RadioGroup>
            </FormControl>
            <ChartNotes notes={['Information collated based on Timekeeper']} actionHint="Drill down to timekeeper details" />
        </div>
    );
};

// tslint:disable-next-line:no-any
export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface FeesBilledVsBudgetQueryParams {
    entityType: number | null;
    entityGuid: string | null;
    responsibility: number | null;
}

export interface FeesBilledVsBudgetQueryData {
    statistics: Statistics;
}

export interface Statistics {
    fees: Fees;
}

export interface Fees {
    feesBilledVsBudget: FeesBilledVsBudget;
}

export interface FeesBilledVsBudget {
    lastFinancialYear: LastFinancialYear;
    lastMonth: LastFinancialYear;
    previousMonth: LastFinancialYear;
    previousFinancialYear: LastFinancialYear;
}

export interface LastFinancialYear {
    summary: Summary;
    feesBilledGrouping: FeesBGrouping[];
    feesBudgetGrouping: FeesBGrouping[];
    financialYearToPreviousMonthSummary?: Summary;
}

export interface FeesBGrouping {
    group: string;
    value: number;
}

export interface Summary {
    lastPeriodTotal: number;
    previousPeriodTotal: number;
    budget: number;
    percentage: number;
    variance: number;
}

const FeesBilledVsBudgetQuery = gql`
    query FeesBilledVsBudgetData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            fees {
                feesBilledVsBudget(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    lastFinancialYear {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            percentage
                            variance
                        }
                        feesBilledGrouping {
                            group
                            value
                        }
                        feesBudgetGrouping {
                            group
                            value
                        }
                    }
                    lastMonth {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            percentage
                            variance
                        }
                        feesBilledGrouping {
                            group
                            value
                        }
                        feesBudgetGrouping {
                            group
                            value
                        }
                    }
                    previousMonth {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            percentage
                            variance
                        }
                        financialYearToPreviousMonthSummary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            percentage
                            variance
                        }
                        feesBilledGrouping {
                            group
                            value
                        }
                        feesBudgetGrouping {
                            group
                            value
                        }
                    }
                    previousFinancialYear {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            percentage
                            variance
                        }
                        feesBilledGrouping {
                            group
                            value
                        }
                        feesBudgetGrouping {
                            group
                            value
                        }
                    }
                }
            }
        }
    }
`;
